/**
 * lib/marksGridPdf.ts
 * ------------------------------------------------------------------
 * A4 landscape mark-sheet PDF: jspdf-autotable for the grid (column
 * sizing, striping, and automatic pagination with a REPEATING column
 * header) + the shared LKC chrome (lib/pdfChrome) for the branded
 * letterhead and footer drawn on every page.
 *
 * Class sections are emitted as full-width band rows inside the table
 * so they flow with pagination; read-only rows (a class teacher's
 * other-teacher groups) are tinted via didParseCell.
 *
 *   buildMarksGridPdf(data).save('marks-Form3A-Accounting-Term1.pdf')
 *   buildMarksGridPdf(data).output('blob')   // email / preview
 *
 * Public interfaces + signature are stable, so callers need no edits.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, {
  type CellHookData,
  type RowInput,
  type Styles,
} from 'jspdf-autotable';
import {
  PDF,
  CONTENT_TOP,
  pageGeom,
  drawLetterhead,
  stampFooters,
} from '@/lib/pdfChrome';

export interface MarksPdfRow {
  no: number;
  name: string;
  ca: Record<string, string>; // CA label -> display value ('' if blank)
  caMark: string;
  exam?: string;
  termMark: string;
  grade: string;
  readOnly?: boolean;
}

export interface MarksPdfClass {
  className: string;
  rows: MarksPdfRow[];
}

export interface MarksPdfData {
  subjectName: string;
  termLabel: string;
  year: string;
  months: string[];
  usesExam: boolean;
  showGrades: boolean;
  classes: MarksPdfClass[];
  formLabel?: string;
  logoDataUrl?: string | null;
  generatedBy?: string;
}

type RowMeta =
  | { type: 'section' }
  | { type: 'row'; ro: boolean; zebra: boolean };

export function buildMarksGridPdf(data: MarksPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

  // ---- columns --------------------------------------------------------
  const head = [
    'No',
    'Student',
    ...data.months,
    'CA',
    ...(data.usesExam ? ['Exam'] : []),
    'Term',
    ...(data.showGrades ? ['Grade'] : []),
  ];
  const nCols = head.length;

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { cellWidth: 10, halign: 'center' },
    1: { cellWidth: 'auto', halign: 'left', fontStyle: 'bold' },
  };
  let ci = 2;
  for (let i = 0; i < data.months.length; i++)
    columnStyles[ci++] = { cellWidth: 15, halign: 'center' };
  columnStyles[ci++] = { cellWidth: 15, halign: 'center' }; // CA
  if (data.usesExam) columnStyles[ci++] = { cellWidth: 15, halign: 'center' };
  columnStyles[ci++] = { cellWidth: 17, halign: 'center' }; // Term
  if (data.showGrades) columnStyles[ci++] = { cellWidth: 15, halign: 'center' };

  // ---- body + per-row metadata ---------------------------------------
  const body: RowInput[] = [];
  const meta: RowMeta[] = [];
  for (const cls of data.classes) {
    body.push([
      {
        content: `${cls.className}    ·    ${cls.rows.length} student${
          cls.rows.length === 1 ? '' : 's'
        }`,
        colSpan: nCols,
        styles: {
          fillColor: PDF.BAND,
          textColor: PDF.NAVY,
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'left',
          cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
        },
      },
    ]);
    meta.push({ type: 'section' });

    cls.rows.forEach((r, idx) => {
      const cells: string[] = [
        String(r.no),
        r.readOnly ? `${r.name}  (read-only)` : r.name,
        ...data.months.map((m) => r.ca[m] || '–'),
        r.caMark || '–',
        ...(data.usesExam ? [r.exam || '–'] : []),
        r.termMark || '–',
        ...(data.showGrades ? [r.grade || '-'] : []),
      ];
      body.push(cells);
      meta.push({
        type: 'row',
        ro: !!r.readOnly,
        zebra: !r.readOnly && idx % 2 === 1,
      });
    });
  }

  const metaLine = [data.subjectName, data.formLabel, `${data.termLabel} ${data.year}`]
    .filter(Boolean)
    .join('  ·  ');

  autoTable(pdf, {
    head: [head],
    body,
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: PDF.INK,
      cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'ellipsize',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'center',
      lineWidth: 0,
    },
    columnStyles,
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: 'MARK SHEET',
        metaLine,
        logoDataUrl: null,
        geom,
      });
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body') return;
      const m = meta[h.row.index];
      if (!m || m.type === 'section') return;
      if (m.ro) {
        h.cell.styles.fillColor = PDF.READONLY_BG;
        h.cell.styles.textColor = PDF.MUTED;
        h.cell.styles.fontStyle = h.column.index === 1 ? 'italic' : 'normal';
      } else if (m.zebra) {
        h.cell.styles.fillColor = PDF.ZEBRA;
      }
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
